import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { collectionsService } from "../../services/collections";
import api from "../../services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { getImageUrl } from "../../utils/imageUtils";
import { cn } from "@/lib/utils";
import { Plus, Pencil, Trash2, Upload, X, Search, Layers, Save, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
});

const CHANNELS = ["edisplay", "funeral", "infinity"];
const CHANNEL_LABEL = { edisplay: "e-Displayroom", funeral: "Funeral Supplies", infinity: "Infinity Touch" };

// Mirror of the backend channelResolution.collectionChannels() fallback: prefer
// the explicit channels[] array, else derive from the legacy is_main_store flag.
const initSel = (col) => {
  const chs = Array.isArray(col.channels) && col.channels.length > 0
    ? col.channels
    : (col.is_main_store ? ["edisplay"] : []);
  return { edisplay: chs.includes("edisplay"), funeral: chs.includes("funeral"), infinity: chs.includes("infinity") };
};

const EMPTY_FORM = { name: "", description: "", cover_image: "", is_active: true, is_featured: false, edisplay: false, funeral: false, infinity: false };

/**
 * Product Management → Collections.
 *
 * One place for the platform owner to manage every collection: assign each to
 * the three sales channels (Edisplay / Funeral Supplies / Infinity Touch),
 * create new collections with a channel selection, and edit/delete metadata.
 *
 * Channels are the source of truth on Collection.channels and are persisted via
 * the shared POST /admin/collection-mapping endpoint (same as Settings → Collection
 * Sync Manager), so product effective_channels recompute instantly. The full
 * collection batch is sent on every save so the PlatformSettings mirror stays
 * complete. Metadata CRUD uses the existing /collections endpoints.
 */
export default function ProductManagementCollections() {
  const [collections, setCollections] = useState([]);
  const [sel, setSel] = useState({});          // { [_id]: {edisplay,funeral,infinity} }
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const fetchCollections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await collectionsService.getAll({ limit: 2000, skip_populate: true });
      const data = res.data || res || [];
      setCollections(data);
      const nextSel = {};
      for (const col of data) nextSel[col._id] = initSel(col);
      setSel(nextSel);
      setDirty(false);
    } catch (e) {
      toast.error("Failed to load collections");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCollections(); }, [fetchCollections]);

  // ---- filtering + client-side pagination ----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return collections;
    return collections.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [collections, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * limit, page * limit),
    [filtered, page, limit]
  );

  // ---- channel toggles ----
  const toggle = (id, channel) => {
    setSel((prev) => ({ ...prev, [id]: { ...prev[id], [channel]: !prev[id]?.[channel] } }));
    setDirty(true);
  };

  // ---- persist channel mappings (full batch) ----
  const buildMappings = (list, selection) =>
    list.map((col) => {
      const s = selection[col._id] || {};
      const channels = CHANNELS.filter((c) => s[c]);
      const base = { name: col.name, handle: col.handle, channels };
      return col.shopify_collection_id
        ? { ...base, shopify_collection_id: col.shopify_collection_id }
        : { ...base, collection_id: col._id };
    });

  const saveMappings = async (list = collections, selection = sel) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/collection-mapping`,
        { mappings: buildMappings(list, selection) },
        getAuthHeaders()
      );
      toast.success("Channel mappings saved. Products reorganized automatically.");
      await fetchCollections();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save channel mappings");
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    if (!window.confirm("Sync products & collections from Shopify now? This may take a moment.")) return;
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/sync-shopify`, {}, getAuthHeaders());
      toast.success("Shopify sync complete.");
      await fetchCollections();
    } catch (e) {
      toast.error(e.response?.data?.message || "Shopify sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // ---- metadata CRUD ----
  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setSheetOpen(true); };

  const openEdit = (col) => {
    const s = sel[col._id] || {};
    setEditing(col);
    setForm({
      name: col.name || "",
      description: col.description || "",
      cover_image: col.cover_image || "",
      is_active: col.is_active !== false,
      is_featured: !!col.is_featured,
      edisplay: !!s.edisplay, funeral: !!s.funeral, infinity: !!s.infinity,
    });
    setSheetOpen(true);
  };

  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("image", file);
    try {
      const res = await api.post(`/upload/collection-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image: res.data.path }));
      toast.success("Image uploaded!");
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const meta = {
        name: form.name,
        description: form.description,
        cover_image: form.cover_image,
        is_active: form.is_active,
        is_featured: form.is_featured,
      };
      const chosen = { edisplay: form.edisplay, funeral: form.funeral, infinity: form.infinity };

      if (editing) {
        await collectionsService.update(editing.collection_id || editing._id, meta);
        // Persist channel changes made in the Edit sheet via the batch endpoint.
        const nextSel = { ...sel, [editing._id]: chosen };
        setSel(nextSel);
        await saveMappings(collections, nextSel);
        toast.success("Collection updated");
      } else {
        const created = await collectionsService.create(meta);
        const newId = created._id || created.collection_id;
        // Re-load so the new collection is in the batch, then apply its channels.
        const res = await collectionsService.getAll({ limit: 2000, skip_populate: true });
        const list = res.data || res || [];
        const nextSel = {};
        for (const col of list) nextSel[col._id] = initSel(col);
        if (newId) nextSel[newId] = chosen;
        await saveMappings(list, nextSel);
        toast.success("Collection created");
      }
      setSheetOpen(false);
      setForm(EMPTY_FORM);
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save collection");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (col) => {
    if (!window.confirm("Delete this collection? Products keep existing but lose this collection tag.")) return;
    try {
      await collectionsService.delete(col.collection_id || col._id);
      toast.success("Collection deleted");
      await fetchCollections();
    } catch (e) {
      toast.error("Failed to delete collection");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pm-collections">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Layers className="w-6 h-6" /> Collections
          </h2>
          <p className="text-slate-500">Assign collections to channels and manage them in one place</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
            <RefreshCw className={cn("w-4 h-4 mr-1", syncing && "animate-spin")} /> Sync from Shopify
          </Button>
          <Button
            size="sm"
            onClick={() => saveMappings()}
            disabled={!dirty || saving}
            className={cn(dirty && "bg-primary hover:bg-primary/90")}
          >
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save Channel Mappings"}
          </Button>
          <Button size="sm" onClick={openNew} className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-1" /> Add Collection
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 max-w-sm">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search collections..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
      </div>

      <div className="card-elevated overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[70px]">Image</TableHead>
              <TableHead>Collection</TableHead>
              <TableHead className="w-[80px] text-center">Products</TableHead>
              {CHANNELS.map((c) => (
                <TableHead key={c} className="text-center">
                  <span className="text-xs">{CHANNEL_LABEL[c]}</span>
                </TableHead>
              ))}
              <TableHead className="w-[90px]">Status</TableHead>
              <TableHead className="text-right w-[90px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            ) : pageRows.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">No collections found</TableCell></TableRow>
            ) : pageRows.map((col) => (
              <TableRow key={col._id} className={cn(!col.is_active && "opacity-60")}>
                <TableCell>
                  {col.cover_image ? (
                    <img src={getImageUrl(col.cover_image)} alt="" className="w-10 h-10 object-cover rounded" />
                  ) : (
                    <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400">
                      <Layers className="w-5 h-5" />
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <p className="font-medium text-slate-900 text-sm">{col.name}</p>
                  {col.description && <p className="text-xs text-slate-400 truncate max-w-xs">{col.description}</p>}
                </TableCell>
                <TableCell className="text-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    {col.product_count || 0}
                  </span>
                </TableCell>
                {CHANNELS.map((c) => (
                  <TableCell key={c} className="text-center">
                    <Checkbox
                      checked={!!sel[col._id]?.[c]}
                      onCheckedChange={() => toggle(col._id, c)}
                      aria-label={`${col.name} ${CHANNEL_LABEL[c]}`}
                    />
                  </TableCell>
                ))}
                <TableCell>
                  <span className={cn(
                    "inline-flex items-center w-fit px-2 py-1 rounded-full text-xs font-medium",
                    col.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                  )}>
                    {col.is_active ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(col)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(col)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {!loading && filtered.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-4">
            <div>
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{" "}
              <span className="font-medium">{Math.min(page * limit, filtered.length)}</span> of{" "}
              <span className="font-medium">{filtered.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>Rows:</span>
              <Select value={limit.toString()} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-8 w-[70px]"><SelectValue /></SelectTrigger>
                <SelectContent side="top">
                  {[15, 25, 50, 100].map((n) => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>

            {/* Numbered Pagination Logic */}
            <div className="flex items-center mx-2 space-x-1">
              {Array.from({ length: totalPages }).map((_, i) => {
                const pageNum = i + 1;
                // Show current page, first page, last page, and adjacent pages
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className={cn(
                        "h-8 w-8 p-0",
                        page === pageNum ? "bg-primary hover:bg-primary/90 text-white" : "text-slate-600 hover:bg-slate-100"
                      )}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                }

                // Ellipses logic
                if (pageNum === page - 2 || pageNum === page + 2) {
                  return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                }

                return null;
              })}
            </div>

            <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</Button>
          </div>
        </div>
      )}

      {/* Add / Edit sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit Collection" : "New Collection"}</SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label htmlFor="name">Collection Name</Label>
              <Input id="name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Memorial Pendants" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Cover Image</Label>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors cursor-pointer relative">
                <input type="file" accept="image/*"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={(e) => uploadCover(e.target.files[0])} />
                {form.cover_image ? (
                  <div className="relative group aspect-video rounded-lg overflow-hidden border border-slate-200">
                    <img src={getImageUrl(form.cover_image)} alt="Cover" className="w-full h-full object-cover" />
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); setForm({ ...form, cover_image: "" }); }}
                      className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full text-slate-600 hover:text-red-600 shadow-sm z-10">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-slate-500 py-4">
                    <Upload className="w-8 h-8" />
                    <p className="text-sm font-medium">Click to upload cover image</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Channels</Label>
              <p className="text-xs text-slate-500">Where this collection's products are available.</p>
              <div className="grid grid-cols-1 gap-2 pt-1">
                {CHANNELS.map((c) => (
                  <div key={c} className="flex items-center space-x-2">
                    <Checkbox id={`ch-${c}`} checked={!!form[c]}
                      onCheckedChange={(v) => setForm({ ...form, [c]: !!v })} />
                    <Label htmlFor={`ch-${c}`} className="cursor-pointer">{CHANNEL_LABEL[c]}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-6 pt-2">
              <div className="flex items-center space-x-2">
                <Checkbox id="is_active" checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} />
                <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="is_featured" checked={form.is_featured}
                  onCheckedChange={(v) => setForm({ ...form, is_featured: !!v })} />
                <Label htmlFor="is_featured" className="cursor-pointer">Featured</Label>
              </div>
            </div>

            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 mt-4" disabled={submitting}>
              {submitting ? "Saving…" : editing ? "Update Collection" : "Create Collection"}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
